import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import AppLayout from "@/components/AppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Trophy, TrendingUp, DollarSign, Target, Sparkles, Crown, Medal, Award } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface RepStats {
  rep_id: string;
  rep_name: string;
  total_leads: number;
  contacted: number;
  qualified: number;
  proposals: number;
  won: number;
  lost: number;
  close_rate: number;
  pipeline_value: number;
  won_value: number;
  avg_deal_size: number;
  commission_earned: number;
}

type Period = "30" | "90" | "365" | "all";

const periodLabels: Record<Period, string> = {
  "30": "Last 30 days",
  "90": "Last 90 days",
  "365": "Last 12 months",
  "all": "All time",
};

export default function SalesLeaderboard() {
  const { orgId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<RepStats[]>([]);
  const [period, setPeriod] = useState<Period>("90");
  const [coachOpen, setCoachOpen] = useState(false);
  const [coachLoading, setCoachLoading] = useState(false);
  const [coachReport, setCoachReport] = useState<any>(null);
  const [coachRepName, setCoachRepName] = useState("");

  useEffect(() => {
    if (orgId) loadStats();
  }, [orgId, period]);

  const loadStats = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const since = period === "all"
        ? null
        : new Date(Date.now() - parseInt(period) * 86400000).toISOString();

      // Employees in org (active reps)
      const { data: emps } = await supabase
        .from("employees")
        .select("id, user_id, full_name, role, status")
        .eq("org_id", orgId)
        .eq("status", "active");

      const repMap = new Map<string, { name: string; emp_id: string }>();
      (emps || []).forEach((e: any) => {
        if (e.user_id) repMap.set(e.user_id, { name: e.full_name || "Unnamed", emp_id: e.id });
      });

      // Leads for org
      let leadsQ = supabase.from("leads").select("id, status, assigned_to, estimated_value, created_at").eq("org_id", orgId);
      if (since) leadsQ = leadsQ.gte("created_at", since);
      const { data: leads } = await leadsQ;

      // Commissions for org
      let commQ = supabase.from("commissions").select("employee_id, commission_amount, status, earned_at").eq("org_id", orgId);
      if (since) commQ = commQ.gte("earned_at", since);
      const { data: comms } = await commQ;

      // Aggregate per rep
      const result: RepStats[] = [];
      repMap.forEach((info, userId) => {
        const myLeads = (leads || []).filter((l: any) => l.assigned_to === userId);
        const won = myLeads.filter((l: any) => l.status === "won");
        const lost = myLeads.filter((l: any) => l.status === "lost");
        const contacted = myLeads.filter((l: any) => l.status !== "new").length;
        const qualified = myLeads.filter((l: any) => l.status === "qualified").length;
        const proposals = myLeads.filter((l: any) => ["proposal_sent", "negotiating"].includes(l.status)).length;
        const wonValue = won.reduce((s: number, l: any) => s + Number(l.estimated_value || 0), 0);
        const pipelineValue = myLeads
          .filter((l: any) => !["won", "lost"].includes(l.status))
          .reduce((s: number, l: any) => s + Number(l.estimated_value || 0), 0);
        const closed = won.length + lost.length;
        const closeRate = closed > 0 ? (won.length / closed) * 100 : 0;
        const avgDeal = won.length > 0 ? wonValue / won.length : 0;
        const commission = (comms || [])
          .filter((c: any) => c.employee_id === info.emp_id && c.status !== "voided")
          .reduce((s: number, c: any) => s + Number(c.commission_amount || 0), 0);

        result.push({
          rep_id: userId,
          rep_name: info.name,
          total_leads: myLeads.length,
          contacted,
          qualified,
          proposals,
          won: won.length,
          lost: lost.length,
          close_rate: closeRate,
          pipeline_value: pipelineValue,
          won_value: wonValue,
          avg_deal_size: avgDeal,
          commission_earned: commission,
        });
      });

      result.sort((a, b) => b.won_value - a.won_value);
      setStats(result);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load leaderboard");
    } finally {
      setLoading(false);
    }
  };

  const teamTotals = useMemo(() => {
    return stats.reduce(
      (acc, r) => ({
        revenue: acc.revenue + r.won_value,
        pipeline: acc.pipeline + r.pipeline_value,
        deals: acc.deals + r.won,
        commissions: acc.commissions + r.commission_earned,
      }),
      { revenue: 0, pipeline: 0, deals: 0, commissions: 0 }
    );
  }, [stats]);

  const runCoach = async (rep: RepStats) => {
    setCoachOpen(true);
    setCoachLoading(true);
    setCoachReport(null);
    setCoachRepName(rep.rep_name);
    try {
      const { data, error } = await supabase.functions.invoke("ai-sales-coach", {
        body: { rep, allReps: stats, orgContext: "Roofing contractor sales team" },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setCoachReport(data.report);
    } catch (e: any) {
      toast.error(e.message || "Coach failed");
      setCoachOpen(false);
    } finally {
      setCoachLoading(false);
    }
  };

  const rankIcon = (idx: number) => {
    if (idx === 0) return <Crown className="h-5 w-5 text-yellow-500" />;
    if (idx === 1) return <Medal className="h-5 w-5 text-slate-400" />;
    if (idx === 2) return <Award className="h-5 w-5 text-amber-700" />;
    return <span className="text-sm font-bold text-muted-foreground w-5 text-center">{idx + 1}</span>;
  };

  return (
    <AppLayout>
      <div className="relative min-h-screen bg-background">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 left-1/3 h-[500px] w-[500px] rounded-full bg-primary/10 blur-[120px]" />
          <div className="absolute top-20 right-0 h-[400px] w-[400px] rounded-full bg-yellow-500/10 blur-[120px]" />
        </div>

        <div className="relative border-b border-primary/20 bg-gradient-to-br from-yellow-500/10 via-background/80 to-primary/10 backdrop-blur-2xl">
          <div className="container mx-auto px-6 py-10">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="space-y-3">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-yellow-500/15 border border-yellow-500/30">
                  <Trophy className="h-3 w-3 text-yellow-600" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-yellow-700 dark:text-yellow-400">
                    Sales Leaderboard
                  </span>
                </div>
                <h1 className="text-5xl font-bold tracking-tight bg-gradient-to-r from-yellow-500 via-primary to-purple-500 bg-clip-text text-transparent">
                  Rep Performance
                </h1>
                <p className="text-base text-muted-foreground max-w-xl">
                  Live KPIs by rep. Get AI coaching to level up the team.
                </p>
              </div>
              <Tabs value={period} onValueChange={(v) => setPeriod(v as Period)}>
                <TabsList>
                  <TabsTrigger value="30">30d</TabsTrigger>
                  <TabsTrigger value="90">90d</TabsTrigger>
                  <TabsTrigger value="365">12mo</TabsTrigger>
                  <TabsTrigger value="all">All</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>
        </div>

        <div className="relative container mx-auto px-6 py-8 space-y-6">
          {/* Team totals */}
          <div className="grid gap-3 md:grid-cols-4">
            <StatTile icon={DollarSign} label="Team Revenue" value={`$${teamTotals.revenue.toLocaleString()}`} accent="from-emerald-500 to-teal-600" />
            <StatTile icon={TrendingUp} label="Open Pipeline" value={`$${teamTotals.pipeline.toLocaleString()}`} accent="from-blue-500 to-cyan-600" />
            <StatTile icon={Target} label="Deals Won" value={teamTotals.deals.toString()} accent="from-purple-500 to-pink-600" />
            <StatTile icon={Trophy} label="Commissions Earned" value={`$${teamTotals.commissions.toLocaleString()}`} accent="from-yellow-500 to-orange-600" />
          </div>

          <Card className="border-border/40 bg-card/40 backdrop-blur-xl overflow-hidden">
            <CardHeader className="border-b border-border/40">
              <CardTitle className="text-base font-semibold">Rep Rankings · {periodLabels[period]}</CardTitle>
              <CardDescription className="text-xs">Sorted by won revenue. Click "AI Coach" for personalized insights.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 space-y-3">
                  {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
              ) : stats.length === 0 ? (
                <div className="p-12 text-center text-sm text-muted-foreground">
                  No active employees with sales activity yet. Add employees in Settings → Team & Commissions.
                </div>
              ) : (
                <div className="divide-y divide-border/40">
                  {stats.map((rep, idx) => (
                    <div
                      key={rep.rep_id}
                      className={cn(
                        "px-6 py-4 hover:bg-accent/30 transition-colors",
                        idx === 0 && "bg-gradient-to-r from-yellow-500/5 to-transparent"
                      )}
                    >
                      <div className="flex items-center gap-4 flex-wrap">
                        <div className="w-8 flex justify-center">{rankIcon(idx)}</div>
                        <div className="flex-1 min-w-[180px]">
                          <div className="font-semibold text-sm">{rep.rep_name}</div>
                          <div className="text-xs text-muted-foreground">
                            {rep.total_leads} leads · {rep.won} won · {rep.lost} lost
                          </div>
                        </div>
                        <Metric label="Close Rate" value={`${rep.close_rate.toFixed(0)}%`} />
                        <Metric label="Pipeline" value={`$${(rep.pipeline_value / 1000).toFixed(1)}k`} />
                        <Metric label="Revenue" value={`$${(rep.won_value / 1000).toFixed(1)}k`} highlight />
                        <Metric label="Avg Deal" value={`$${(rep.avg_deal_size / 1000).toFixed(1)}k`} />
                        <Metric label="Commission" value={`$${(rep.commission_earned / 1000).toFixed(1)}k`} />
                        <Button size="sm" variant="outline" onClick={() => runCoach(rep)} className="gap-1.5">
                          <Sparkles className="h-3.5 w-3.5 text-primary" />
                          AI Coach
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Sheet open={coachOpen} onOpenChange={setCoachOpen}>
          <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                AI Coach · {coachRepName}
              </SheetTitle>
            </SheetHeader>
            <div className="mt-6 space-y-5">
              {coachLoading && (
                <div className="space-y-3">
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-20 w-full" />
                  <Skeleton className="h-20 w-full" />
                </div>
              )}
              {coachReport && (
                <>
                  <div className="rounded-xl border border-primary/30 bg-primary/5 p-4">
                    <div className="text-[10px] uppercase tracking-wider text-primary font-bold mb-1">Verdict</div>
                    <div className="text-sm font-medium">{coachReport.verdict}</div>
                    <div className="mt-2 flex items-center gap-2">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Coaching Score</div>
                      <Badge variant="secondary" className="bg-primary/10 text-primary">{coachReport.coaching_score}/100</Badge>
                    </div>
                  </div>

                  <Section title="Strengths" tone="success">
                    <ul className="space-y-1.5">
                      {coachReport.strengths?.map((s: string, i: number) => (
                        <li key={i} className="text-sm flex gap-2"><span className="text-emerald-500">✓</span>{s}</li>
                      ))}
                    </ul>
                  </Section>

                  <Section title="Gaps" tone="warning">
                    <ul className="space-y-1.5">
                      {coachReport.gaps?.map((g: string, i: number) => (
                        <li key={i} className="text-sm flex gap-2"><span className="text-orange-500">!</span>{g}</li>
                      ))}
                    </ul>
                  </Section>

                  <Section title="Next Actions">
                    <div className="space-y-3">
                      {coachReport.next_actions?.map((a: any, i: number) => (
                        <div key={i} className="rounded-lg border border-border/60 p-3 bg-background/40">
                          <div className="font-semibold text-sm">{a.title}</div>
                          <div className="text-xs text-muted-foreground mt-1"><b>Why:</b> {a.why}</div>
                          <div className="text-xs text-muted-foreground mt-1"><b>How:</b> {a.how}</div>
                        </div>
                      ))}
                    </div>
                  </Section>
                </>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </AppLayout>
  );
}

function StatTile({ icon: Icon, label, value, accent }: any) {
  return (
    <Card className="border-border/40 bg-card/40 backdrop-blur-xl overflow-hidden">
      <CardContent className="p-4 flex items-center gap-3">
        <div className={cn("h-10 w-10 rounded-lg bg-gradient-to-br flex items-center justify-center shadow-lg ring-1 ring-white/10", accent)}>
          <Icon className="h-5 w-5 text-white" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">{label}</div>
          <div className="text-xl font-bold">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function Metric({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="min-w-[80px]">
      <div className="text-[9px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">{label}</div>
      <div className={cn("text-sm font-bold tabular-nums", highlight && "text-emerald-600 dark:text-emerald-400")}>{value}</div>
    </div>
  );
}

function Section({ title, tone, children }: { title: string; tone?: "success" | "warning"; children: React.ReactNode }) {
  return (
    <div>
      <div className={cn(
        "text-[10px] uppercase tracking-[0.15em] font-bold mb-2",
        tone === "success" && "text-emerald-600 dark:text-emerald-400",
        tone === "warning" && "text-orange-600 dark:text-orange-400",
        !tone && "text-foreground"
      )}>{title}</div>
      {children}
    </div>
  );
}
